import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';
import { PurchaseOrder, PurchaseOrderStatus } from '@bloomays-lib/types.shared';

export type MissionPurchaseOrderIndicator = 'notRequired' | 'ok' | 'missing' | 'expired' | 'exhausted';

/**
 * Recalcule le statut effectif d'un BDC à une date donnée :
 *  - épuisé si le montant facturé HT ≥ montant total HT ;
 *  - expiré si la date de fin est dépassée ;
 *  - sinon : statut stocké (`received` en régime nominal ; `waiting` données historiques).
 *
 * NB : règle alignée sur `deriveStatusForReferenceDate` (api.bloomers) et
 * `derivePurchaseOrderStatus` (adapters.airtable), volontairement dupliquée
 * côté front pour ne pas dépendre d'un appel back additionnel.
 */
export const derivePurchaseOrderStatusForDate = (
  purchaseOrder: Pick<PurchaseOrder, 'status' | 'endDate' | 'amountHT' | 'invoicedAmountHT'>,
  referenceDate: Date = new Date(),
): PurchaseOrderStatus => {
  const invoicedAmountHT = purchaseOrder.invoicedAmountHT ?? 0;
  if (
    purchaseOrder.amountHT !== undefined &&
    purchaseOrder.amountHT !== null &&
    invoicedAmountHT >= purchaseOrder.amountHT
  ) {
    return PurchaseOrderStatus.soldout;
  }
  if (purchaseOrder.endDate && isBefore(startOfDay(new Date(purchaseOrder.endDate)), startOfDay(referenceDate))) {
    return PurchaseOrderStatus.expired;
  }
  return purchaseOrder.status ?? PurchaseOrderStatus.waiting;
};

/**
 * Indicateur synthétique pour la fiche mission :
 *  - `notRequired` : aucun BDC requis (mission + client).
 *  - `ok` : au moins un BDC actif (received) couvre la mission à date.
 *  - `expired` : tous les BDC liés ont une date de fin dépassée.
 *  - `exhausted` : tous les BDC liés sont épuisés (facturé ≥ total).
 *  - `missing` : aucun BDC lié, ou tous en attente.
 *
 * `effectivePurchaseOrderRequired` doit être passé tel qu'il est exposé par le
 * résolveur API (`IMission.effectivePurchaseOrderRequired`).
 */
export const computeMissionPurchaseOrderIndicator = (params: {
  effectivePurchaseOrderRequired: boolean | undefined | null;
  purchaseOrders: PurchaseOrder[] | undefined | null;
  referenceDate?: Date;
}): MissionPurchaseOrderIndicator => {
  const { effectivePurchaseOrderRequired, purchaseOrders, referenceDate = new Date() } = params;
  if (!effectivePurchaseOrderRequired) {
    return 'notRequired';
  }
  const linked = purchaseOrders ?? [];
  if (linked.length === 0) {
    return 'missing';
  }
  const derivedStatuses = linked.map((purchaseOrder) => derivePurchaseOrderStatusForDate(purchaseOrder, referenceDate));
  if (derivedStatuses.some((status) => status === PurchaseOrderStatus.received)) {
    return 'ok';
  }
  if (derivedStatuses.every((status) => status === PurchaseOrderStatus.soldout)) {
    return 'exhausted';
  }
  if (derivedStatuses.every((status) => status === PurchaseOrderStatus.expired)) {
    return 'expired';
  }
  return 'missing';
};
